import { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Upload, X, GripVertical, Loader2, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Image Item Component
function SortableImage({ id, url, thumb, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group aspect-square bg-muted border border-border overflow-hidden"
    >
      <img 
        src={thumb || url} 
        alt="Galerie" 
        className="w-full h-full object-cover"
      />
      {/* Drag Handle - always visible on mobile, touch-none only here */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-0 left-0 p-3 bg-black/60 rounded-br cursor-grab active:cursor-grabbing sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-none"
      >
        <GripVertical size={18} className="text-white" />
      </div>
      {/* Remove Button - always visible on mobile */}
      <button
        type="button"
        onClick={() => onRemove(id)}
        className="absolute top-0 right-0 p-2.5 bg-red-600 rounded-bl text-white sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-red-700"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function ImageGalleryUpload({ images = [], onChange, folder = 'events/gallery' }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getImgUrl = (img) => typeof img === 'string' ? img : (img?.url || img);
  const getThumbUrl = (img) => typeof img === 'string' ? img : (img?.thumb || img?.url || img);

  const imageItems = images.map((img, index) => ({
    id: `img-${index}-${getImgUrl(img).slice(-20)}`,
    url: getImgUrl(img),
    thumb: getThumbUrl(img),
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = imageItems.findIndex(item => item.id === active.id);
      const newIndex = imageItems.findIndex(item => item.id === over.id);
      
      const newOrder = arrayMove(images, oldIndex, newIndex);
      onChange(newOrder);
    }
  };

  const handleRemove = (id) => {
    const index = imageItems.findIndex(item => item.id === id);
    if (index !== -1) {
      const newImages = [...images];
      newImages.splice(index, 1);
      onChange(newImages);
    }
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    
    setUploading(true);
    const token = localStorage.getItem('shrt_token');
    const newImages = [...images];

    for (const file of files) {
      // Skip non-image files
      if (!file.type.startsWith('image/')) continue;
      
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/admin/upload/r2?folder=${encodeURIComponent(folder)}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        if (data.url) {
          newImages.push(data.thumb ? { url: data.url, thumb: data.thumb } : data.url);
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }

    onChange(newImages);
    setUploading(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  }, [images, onChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    uploadFiles(files);
    e.target.value = '';
  };

  return (
    <div className="space-y-3">
      {/* Dropzone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
          ${dragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-border hover:border-primary/50 hover:bg-muted/50'
          }
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
        onClick={() => document.getElementById('gallery-file-input')?.click()}
      >
        <input
          id="gallery-file-input"
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Bilder werden hochgeladen...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className={`p-3 rounded-full ${dragActive ? 'bg-primary/10' : 'bg-muted'}`}>
              {dragActive ? (
                <ImageIcon className="w-6 h-6 text-primary" />
              ) : (
                <Upload className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium">
                {dragActive ? 'Bilder hier ablegen' : 'Bilder hier ablegen oder klicken'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Mehrere Bilder können gleichzeitig hochgeladen werden
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Image Grid with Drag & Drop Sorting */}
      {imageItems.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-2">
            {imageItems.length} {imageItems.length === 1 ? 'Bild' : 'Bilder'} • Ziehen zum Sortieren
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={imageItems.map(item => item.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {imageItems.map((item) => (
                  <SortableImage
                    key={item.id}
                    id={item.id}
                    url={item.url}
                    thumb={item.thumb}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
